import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAttributionPayload } from '../utils/attribution';
import { getDeviceFingerprintSync } from '../utils/deviceFingerprint';

// API base URL configuration
// For client-side requests, use /api/v1 prefix which will be handled by the proxy
// The proxy will forward these requests to the backend
const API_URL = '/api/v1';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// --- Silent token refresh logic ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('isAdmin');
  // Don't redirect to /login if we're on an OAuth callback page —
  // the OAuth flow will handle authentication itself
  const path = window.location.pathname;
  if (path.startsWith('/auth/') || path.includes('/authorized') || path.includes('/login/google') || path.includes('/login/yandex')) {
    return;
  }
  window.location.href = '/login';
};

// Add response interceptor with silent refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh on 401 (expired/invalid token)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      // No refresh token available — go to login
      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another request is already refreshing — queue this one
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Use raw axios (not apiClient) to avoid interceptor loop
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const newAccessToken = response.data.access_token;
        const newRefreshToken = response.data.refresh_token;

        localStorage.setItem('token', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        // Retry all queued requests with new token
        processQueue(null, newAccessToken);

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear everything and redirect to login
        processQueue(refreshError as AxiosError, null);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 for backward compatibility during transition:
    // Old backend returns 403 for expired tokens with specific detail message
    if (error.response?.status === 403) {
      const detail = (error.response?.data as any)?.detail;
      if (detail === 'Could not validate credentials') {
        clearAuthAndRedirect();
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    return apiClient.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  
  oauthLogin: (provider: string, oauthId: string, email: string, name: string): Promise<AxiosResponse> => {
    return apiClient.post('/auth/oauth/login', { provider, oauth_id: oauthId, email, name });
  },
  
  getGoogleAuthUrl: (): string => {
    return buildOAuthLoginUrl('google');
  },

  getYandexAuthUrl: (): string => {
    return buildOAuthLoginUrl('yandex');
  },

  getGeoInfo: (): Promise<AxiosResponse<{ country: string | null; google_oauth_blocked: boolean }>> => {
    return apiClient.get('/auth/geo');
  },

  sendEmailLink: (email: string): Promise<AxiosResponse> => {
    return apiClient.post('/auth/email/login', { email });
  },
  
  register: (email: string, name: string, password: string): Promise<AxiosResponse> => {
    return apiClient.post('/auth/register', { email, name, password });
  },
  
  me: (): Promise<AxiosResponse> => {
    return apiClient.get('/users/me');
  },
};

// User API
export const userAPI = {
  getProfile: (): Promise<AxiosResponse> => {
    return apiClient.get('/users/me');
  },
  
  updateProfile: (data: any): Promise<AxiosResponse> => {
    return apiClient.patch('/users/me', data);
  },
  
  changePassword: (oldPassword: string, newPassword: string): Promise<AxiosResponse> => {
    return apiClient.post('/users/me/change-password', { old_password: oldPassword, new_password: newPassword });
  },
};

// Report API
export const reportAPI = {
  getReports: (): Promise<AxiosResponse> => {
    return apiClient.get('/reports');
  },

  getReportsList: (folderId?: number | null): Promise<AxiosResponse> => {
    const params = folderId !== undefined && folderId !== null ? `?folder_id=${folderId}` : '';
    return apiClient.get(`/reports/list${params}`);
  },

  getAllReports: (): Promise<AxiosResponse> => {
    return apiClient.get('/reports/all');
  },
  
  getReport: (id: number): Promise<AxiosResponse> => {
    return apiClient.get(`/reports/${id}`);
  },
  
  createReport: (file: File, reportType: string): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('report_type', reportType);
    
    return apiClient.post('/reports', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  deleteReport: (id: number): Promise<AxiosResponse> => {
    return apiClient.delete(`/reports/${id}`);
  },

  setFeedback: (id: number, feedback: string | null): Promise<AxiosResponse> => {
    return apiClient.patch(`/reports/${id}/feedback`, { feedback });
  },
  
  getTaskStatus: (taskId: string): Promise<AxiosResponse> => {
    return apiClient.get(`/reports/task/${taskId}`);
  },
};

// Step-by-step Report API
export const reportStepsAPI = {
  // Инициализация задачи создания отчета
  initReportTask: (reportType: string, isPro: boolean = false): Promise<AxiosResponse> => {
    console.log('[API] initReportTask - начало инициализации задачи:', { reportType, isPro });

    const formData = new FormData();
    formData.append('report_type', reportType);
    formData.append('is_pro', String(isPro));

    console.log('[API] initReportTask - отправка запроса на /report-steps/init');
    return apiClient.post('/report-steps/init', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] initReportTask - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] initReportTask - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Загрузка Excel-файла
  uploadExcelFile: (taskId: string, file: File): Promise<AxiosResponse> => {
    console.log('[API] uploadExcelFile - начало загрузки файла:', { taskId, fileName: file.name });
    
    const formData = new FormData();
    formData.append('task_id', taskId);
    formData.append('file', file);
    
    console.log('[API] uploadExcelFile - отправка запроса на /report-steps/upload-excel');
    return apiClient.post('/report-steps/upload-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] uploadExcelFile - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] uploadExcelFile - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Генерация аналитических данных
  generateAnalytics: (taskId: string, clientId?: string): Promise<AxiosResponse> => {
    console.log('[API] generateAnalytics - начало генерации аналитики:', { taskId, clientId });
    
    const formData = new FormData();
    formData.append('task_id', taskId);
    if (clientId) {
      console.log('[API] generateAnalytics - добавляем client_id в formData:', clientId);
      formData.append('client_id', clientId);
    } else {
      console.warn('[API] generateAnalytics - client_id не определен!');
    }
    
    console.log('[API] generateAnalytics - отправка запроса на /report-steps/generate-analytics');
    return apiClient.post('/report-steps/generate-analytics', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] generateAnalytics - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] generateAnalytics - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Переключение PRO-режима задачи
  setTaskProMode: (taskId: string, isPro: boolean = true): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('is_pro', String(isPro));
    return apiClient.patch(`/report-steps/task/${taskId}/set-pro`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Обработка отдельного отчета
  processReport: (taskId: string, reportIndex: number, clientId?: string): Promise<AxiosResponse> => {
    console.log('[API] processReport - начало обработки отчета:', { taskId, reportIndex, clientId });
    
    const formData = new FormData();
    formData.append('task_id', taskId);
    formData.append('report_index', reportIndex.toString());
    if (clientId) {
      formData.append('client_id', clientId);
    }
    
    console.log('[API] processReport - отправка запроса на /report-steps/process-report');
    return apiClient.post('/report-steps/process-report', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] processReport - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] processReport - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Обработка всех отчетов
  processAllReports: (taskId: string, clientId?: string): Promise<AxiosResponse> => {
    console.log('[API] processAllReports - начало обработки всех отчетов:', { taskId, clientId });
    
    const formData = new FormData();
    formData.append('task_id', taskId);
    if (clientId) {
      console.log('[API] processAllReports - добавляем client_id в formData:', clientId);
      formData.append('client_id', clientId);
    } else {
      console.warn('[API] processAllReports - client_id не определен!');
    }
    
    console.log('[API] processAllReports - отправка запроса на /report-steps/process-all-reports');
    return apiClient.post('/report-steps/process-all-reports', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] processAllReports - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] processAllReports - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Получение статуса задачи с отчетами и результатами
  getTaskStatusWithReports: (taskId: string): Promise<AxiosResponse> => {
    console.log('[API] getTaskStatusWithReports - запрос статуса задачи с отчетами:', { taskId });
    
    return apiClient.get(`/report-steps/task/${taskId}/status`)
    .then(response => {
      console.log('[API] getTaskStatusWithReports - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] getTaskStatusWithReports - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Генерация пользовательского отчета
  generateCustomReport: (taskId: string, query: string, clientId?: string): Promise<AxiosResponse> => {
    console.log('[API] generateCustomReport - начало генерации пользовательского отчета:', { taskId, query, clientId });
    
    const formData = new FormData();
    formData.append('task_id', taskId);
    formData.append('query', query);
    if (clientId) {
      console.log('[API] generateCustomReport - добавляем client_id в formData:', clientId);
      formData.append('client_id', clientId);
    } else {
      console.warn('[API] generateCustomReport - client_id не определен!');
    }
    
    console.log('[API] generateCustomReport - отправка запроса на /report-steps/generate-custom-report');
    return apiClient.post('/report-steps/generate-custom-report', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] generateCustomReport - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] generateCustomReport - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Получение статуса задачи
  getTaskStatus: (taskId: string): Promise<AxiosResponse> => {
    console.log('[API] getTaskStatus - запрос статуса задачи:', { taskId });
    
    return apiClient.get(`/report-steps/task/${taskId}`)
    .then(response => {
      console.log('[API] getTaskStatus - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] getTaskStatus - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Получение списка отчетов для задачи
  getTaskReports: (taskId: string): Promise<AxiosResponse> => {
    console.log('[API] getTaskReports - запрос списка отчетов для задачи:', { taskId });
    
    return apiClient.get(`/report-steps/task/${taskId}/reports`)
    .then(response => {
      console.log('[API] getTaskReports - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] getTaskReports - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Получение аналитических данных для задачи
  getTaskAnalytics: (taskId: string): Promise<AxiosResponse> => {
    console.log('[API] getTaskAnalytics - запрос аналитических данных для задачи:', { taskId });
    
    return apiClient.get(`/report-steps/task/${taskId}/analytics`)
    .then(response => {
      console.log('[API] getTaskAnalytics - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] getTaskAnalytics - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Получение результата генерации отчета
  getReportResult: (taskId: string, reportIndex: number): Promise<AxiosResponse> => {
    console.log('[API] getReportResult - запрос результата генерации отчета:', { taskId, reportIndex });
    
    return apiClient.get(`/report-steps/task/${taskId}/report/${reportIndex}/result`)
    .then(response => {
      console.log('[API] getReportResult - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] getReportResult - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Обновление статуса отчета (удален/восстановлен)
  updateReportStatus: (taskId: string, reportIndex: number, deleted: boolean): Promise<AxiosResponse> => {
    console.log('[API] updateReportStatus - обновление статуса отчета:', { taskId, reportIndex, deleted });
    
    const formData = new FormData();
    formData.append('deleted', deleted.toString());
    
    return apiClient.post(`/report-steps/task/${taskId}/report/${reportIndex}/status`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] updateReportStatus - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] updateReportStatus - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Получение содержимого листа Excel для предпросмотра
  getSheetPreview: (taskId: string, sheetName: string, limit: number = 100, offset: number = 0): Promise<AxiosResponse> => {
    return apiClient.get(`/report-steps/task/${taskId}/sheet/${encodeURIComponent(sheetName)}`, {
      params: { limit, offset },
    });
  },

  // Получение списка задач пользователя с незавершенными отчетами
  getUserTasks: (limit: number = 10): Promise<AxiosResponse> => {
    console.log('[API] getUserTasks - запрос списка задач пользователя:', { limit });
    
    return apiClient.get(`/report-steps/user-tasks?limit=${limit}`)
    .then(response => {
      console.log('[API] getUserTasks - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] getUserTasks - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  // Удаление незавершенной задачи пользователя
  deleteUserTask: (taskId: string): Promise<AxiosResponse> => {
    console.log('[API] deleteUserTask - удаление задачи:', taskId);
    return apiClient.delete(`/report-steps/user-tasks/${taskId}`);
  },

  // Получение прогресса задачи (progress + items)
  getTaskProgress: (taskId: string): Promise<AxiosResponse> => {
    return apiClient.get(`/report-steps/task/${taskId}/progress`);
  },

  // Получение стоимости генерации отчета
  getReportGenerationCost: (): Promise<AxiosResponse> => {
    console.log('[API] getReportGenerationCost - запрос стоимости генерации отчета');
    
    return apiClient.get('/report-steps/cost')
    .then(response => {
      console.log('[API] getReportGenerationCost - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] getReportGenerationCost - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
};


// Admin API
export const adminAPI = {
  getUsers: (): Promise<AxiosResponse> => {
    return apiClient.get('/admin/users');
  },
  
  getReports: (): Promise<AxiosResponse> => {
    // Используем новый эндпоинт /reports/all для получения всех отчетов
    return apiClient.get('/reports/all');
  },
  
  getStats: (): Promise<AxiosResponse> => {
    return apiClient.get('/admin/stats');
  },
  
  toggleUserActive: (userId: number): Promise<AxiosResponse> => {
    return apiClient.patch(`/admin/users/${userId}/toggle-active`, {});
  },
  
  toggleUserAdmin: (userId: number): Promise<AxiosResponse> => {
    return apiClient.patch(`/admin/users/${userId}/toggle-admin`, {});
  },
  
  updateUserBalance: (userId: number, ticks: number): Promise<AxiosResponse> => {
    return apiClient.patch(`/admin/users/${userId}/balance?ticks=${ticks}`, {});
  },
  
  deleteReport: (reportId: number): Promise<AxiosResponse> => {
    return apiClient.delete(`/admin/reports/${reportId}`);
  },
  
  getTickLogs: (userId: number): Promise<AxiosResponse> => {
    return apiClient.get(`/admin/tick-logs/${userId}`);
  },
  
  getEnvVars: (): Promise<AxiosResponse> => {
    return apiClient.get('/admin/env');
  },
  
  updateEnvVars: (envData: Record<string, string>): Promise<AxiosResponse> => {
    return apiClient.post('/admin/env', envData);
  },
  
  executeSql: (sql: string): Promise<AxiosResponse> => {
    return apiClient.post('/admin/db/execute', { sql });
  },
  
  getTables: (): Promise<AxiosResponse> => {
    return apiClient.get('/admin/db/tables');
  },

  // Promo codes admin
  getPromoCodes: (skip: number = 0, limit: number = 50, filterStatus?: string): Promise<AxiosResponse> => {
    let url = `/admin/promo-codes?skip=${skip}&limit=${limit}`;
    if (filterStatus) url += `&filter_status=${filterStatus}`;
    return apiClient.get(url);
  },
  createPromoCode: (data: { code: string; description?: string; ticks_amount: number; max_uses?: number; expires_at?: string; is_active?: boolean }): Promise<AxiosResponse> => {
    return apiClient.post('/admin/promo-codes', data);
  },
  getPromoCode: (id: number): Promise<AxiosResponse> => {
    return apiClient.get(`/admin/promo-codes/${id}`);
  },
  updatePromoCode: (id: number, data: Record<string, any>): Promise<AxiosResponse> => {
    return apiClient.patch(`/admin/promo-codes/${id}`, data);
  },
  deletePromoCode: (id: number): Promise<AxiosResponse> => {
    return apiClient.delete(`/admin/promo-codes/${id}`);
  },

  // Newsletter
  newsletterPreview: (subject: string, body: string): Promise<AxiosResponse> => {
    return apiClient.post('/admin/newsletter/preview', { subject, body });
  },
  newsletterTest: (subject: string, body: string, test_email: string): Promise<AxiosResponse> => {
    return apiClient.post('/admin/newsletter/test', { subject, body, test_email });
  },
  newsletterSend: (subject: string, body: string, audience: string, custom_emails?: string[]): Promise<AxiosResponse> => {
    return apiClient.post('/admin/newsletter/send', { subject, body, audience, custom_emails });
  },
  getCampaigns: (skip: number = 0, limit: number = 50): Promise<AxiosResponse> => {
    return apiClient.get(`/admin/newsletter/campaigns?skip=${skip}&limit=${limit}`);
  },
  getCampaign: (id: number): Promise<AxiosResponse> => {
    return apiClient.get(`/admin/newsletter/campaigns/${id}`);
  },
  getAudienceCount: (audience: string): Promise<AxiosResponse> => {
    return apiClient.get(`/admin/newsletter/audience-count?audience=${audience}`);
  },

  // Email chains
  getChains: (): Promise<AxiosResponse> => {
    return apiClient.get('/admin/email-chains');
  },
  getChainTemplates: (chainType: string): Promise<AxiosResponse> => {
    return apiClient.get(`/admin/email-chains/${chainType}/templates`);
  },
  updateChainTemplate: (chainType: string, stepIndex: number, data: { subject?: string; body?: string }): Promise<AxiosResponse> => {
    return apiClient.patch(`/admin/email-chains/${chainType}/templates/${stepIndex}`, data);
  },
  testChainTemplate: (chainType: string, stepIndex: number, test_email: string): Promise<AxiosResponse> => {
    return apiClient.post(`/admin/email-chains/${chainType}/templates/${stepIndex}/test`, { test_email });
  },
  getMatchingUsers: (chainType: string): Promise<AxiosResponse> => {
    return apiClient.get(`/admin/email-chains/${chainType}/matching-users`);
  },
  startChain: (chainType: string, user_ids: number[]): Promise<AxiosResponse> => {
    return apiClient.post(`/admin/email-chains/${chainType}/start`, { user_ids });
  },
  getActiveEnrollments: (skip: number = 0, limit: number = 50, chainType?: string, statusFilter?: string): Promise<AxiosResponse> => {
    let url = `/admin/email-chains/active?skip=${skip}&limit=${limit}`;
    if (chainType) url += `&chain_type=${chainType}`;
    if (statusFilter) url += `&status_filter=${statusFilter}`;
    return apiClient.get(url);
  },
  cancelEnrollment: (id: number): Promise<AxiosResponse> => {
    return apiClient.delete(`/admin/email-chains/enrollments/${id}`);
  },
  toggleChainAuto: (chainType: string, autoEnabled: boolean): Promise<AxiosResponse> => {
    return apiClient.patch(`/admin/email-chains/${chainType}/settings?auto_enabled=${autoEnabled}`);
  },
};

// Payment API
export const paymentAPI = {
  getPaymentInfo: (): Promise<AxiosResponse> => {
    console.log('[API] getPaymentInfo - запрос информации о платежах');
    return apiClient.get('/payment/info')
      .then(response => {
        console.log('[API] getPaymentInfo - получен ответ:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] getPaymentInfo - ошибка:', error.response?.data || error.message);
        throw error;
      });
  },
  
  getTickPackages: (): Promise<AxiosResponse> => {
    console.log('[API] getTickPackages - запрос пакетов тиков');
    return apiClient.get('/payment/packages')
      .then(response => {
        console.log('[API] getTickPackages - получен ответ:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] getTickPackages - ошибка:', error.response?.data || error.message);
        throw error;
      });
  },
  
  createPayment: (ticks: number): Promise<AxiosResponse> => {
    console.log('[API] createPayment - создание платежа:', { ticks });
    
    const formData = new FormData();
    formData.append('ticks', ticks.toString());
    
    return apiClient.post('/payment/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(response => {
      console.log('[API] createPayment - получен ответ:', response.data);
      return response;
    })
    .catch(error => {
      console.error('[API] createPayment - ошибка:', error.response?.data || error.message);
      throw error;
    });
  },
  
  checkPaymentStatus: (paymentId: string): Promise<AxiosResponse> => {
    console.log('[API] checkPaymentStatus - проверка статуса платежа:', { paymentId });
    
    return apiClient.get(`/payment/status/${paymentId}`)
      .then(response => {
        console.log('[API] checkPaymentStatus - получен ответ:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] checkPaymentStatus - ошибка:', error.response?.data || error.message);
        throw error;
      });
  },
  
  getLastPendingPayment: (): Promise<AxiosResponse> => {
    console.log('[API] getLastPendingPayment - запрос последнего незавершенного платежа');
    
    return apiClient.get('/payment/last-pending')
      .then(response => {
        console.log('[API] getLastPendingPayment - получен ответ:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] getLastPendingPayment - ошибка:', error.response?.data || error.message);
        throw error;
      });
  },
  
  checkPendingPayments: (): Promise<AxiosResponse> => {
    console.log('[API] checkPendingPayments - проверка незавершенных платежей');
    
    return apiClient.get('/payment/check-pending')
      .then(response => {
        console.log('[API] checkPendingPayments - получен ответ:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] checkPendingPayments - ошибка:', error.response?.data || error.message);
        throw error;
      });
  },
};

// Folder API
export const folderAPI = {
  getFolders: (): Promise<AxiosResponse> => {
    return apiClient.get('/folders');
  },
  createFolder: (data: { name: string; color?: string }): Promise<AxiosResponse> => {
    return apiClient.post('/folders', data);
  },
  updateFolder: (id: number, data: { name?: string; color?: string; position?: number }): Promise<AxiosResponse> => {
    return apiClient.patch(`/folders/${id}`, data);
  },
  deleteFolder: (id: number): Promise<AxiosResponse> => {
    return apiClient.delete(`/folders/${id}`);
  },
  moveReportToFolder: (reportId: number, folderId: number | null): Promise<AxiosResponse> => {
    return apiClient.patch(`/reports/${reportId}/folder`, { folder_id: folderId });
  },
  aiOrganize: (): Promise<AxiosResponse> => {
    return apiClient.post('/folders/ai-organize');
  },
  aiOrganizeApply: (data: { suggestions: any[] }): Promise<AxiosResponse> => {
    return apiClient.post('/folders/ai-organize/apply', data);
  },
};

// Promo Code API
export const promoAPI = {
  redeem: (code: string): Promise<AxiosResponse> => {
    return apiClient.post('/promo/redeem', { code });
  },
};

// Referral / Affiliate API
export interface ReferralSummary {
  referral_code: string;
  share_url: string;
  invited_count: number;
  confirmed_invited_count: number;
  earned_ticks_total: number;
  earned_rub_total: string;
  bonus_balance_rub: string;
  pending_rub: string;
  paid_out_rub: string;
  is_suspicious: boolean;
  rub_to_ticks_rate: number;
}

export interface RefereeItem {
  id: number;
  email_masked: string;
  created_at: string;
  is_suspicious: boolean;
  payments_count: number;
  ticks_spent: number;
  cash_rebate_pending: string;
  cash_rebate_confirmed: string;
}

export interface BonusLogItem {
  id: number;
  operation_type: string;
  status: string;
  amount: string;
  description?: string;
  created_at: string;
  confirmed_at?: string;
  referee_email_masked?: string;
}

export const referralAPI = {
  getSummary: (): Promise<ReferralSummary> => {
    return apiClient.get('/referrals/summary').then(r => r.data);
  },
  listReferees: (limit = 50, offset = 0): Promise<RefereeItem[]> => {
    return apiClient.get('/referrals/referees', { params: { limit, offset } }).then(r => r.data);
  },
  listHistory: (limit = 100, offset = 0): Promise<BonusLogItem[]> => {
    return apiClient.get('/referrals/history', { params: { limit, offset } }).then(r => r.data);
  },
  convertToTicks: (amountRub: string | number): Promise<{ ticks_added: number; bonus_balance_rub_after: string }> => {
    return apiClient.post('/referrals/convert-to-ticks', { amount_rub: amountRub }).then(r => r.data);
  },
};

// Admin Referrals API
export interface AdminTopPartner {
  partner_id: number;
  email: string;
  referral_code?: string;
  invited_count: number;
  earned_rub_total: string;
  bonus_balance_rub: string;
  last_payout_at?: string;
}

export interface AdminPair {
  referee_id: number;
  referrer_id: number;
  referrer_email: string;
  referee_email: string;
  referee_created_at: string;
  is_suspicious: boolean;
  risk_score: number;
  risk_signals: string[];
  welcome_bonus_granted: number;
}

export const adminReferralAPI = {
  getTop: (limit = 20): Promise<AdminTopPartner[]> => {
    return apiClient.get('/admin/referrals/top', { params: { limit } }).then(r => r.data);
  },
  listPairs: (params: {
    is_suspicious?: boolean;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ items: AdminPair[]; total: number }> => {
    return apiClient.get('/admin/referrals/pairs', { params }).then(r => r.data);
  },
  getPair: (refereeId: number): Promise<AdminPair> => {
    return apiClient.get(`/admin/referrals/pairs/${refereeId}`).then(r => r.data);
  },
  updatePair: (refereeId: number, body: { is_suspicious: boolean; note?: string }): Promise<AdminPair> => {
    return apiClient.patch(`/admin/referrals/pairs/${refereeId}`, body).then(r => r.data);
  },
  getPartnerDetail: (partnerId: number): Promise<{
    partner: AdminTopPartner;
    referees: RefereeItem[];
    bonus_log: BonusLogItem[];
  }> => {
    return apiClient.get(`/admin/referrals/${partnerId}`).then(r => r.data);
  },
  createPayout: (partnerId: number, body: { amount_rub: string | number; note?: string }): Promise<BonusLogItem> => {
    return apiClient.post(`/admin/referrals/${partnerId}/payout`, body).then(r => r.data);
  },
};

// Excel Documents API
export const documentsAPI = {
  listDocuments: (folderId?: number | null): Promise<any[]> => {
    const params: any = {};
    if (folderId !== null && folderId !== undefined) params.folder_id = folderId;
    return apiClient.get('/documents', { params }).then(r => r.data);
  },
  getDocument: (id: number): Promise<any> => {
    return apiClient.get(`/documents/${id}`).then(r => r.data);
  },
  createDocument: (data: { name: string; sheet_data?: any; folder_id?: number | null }): Promise<any> => {
    return apiClient.post('/documents', data).then(r => r.data);
  },
  updateDocument: (id: number, data: { name?: string; sheet_data?: any }): Promise<any> => {
    return apiClient.put(`/documents/${id}`, data).then(r => r.data);
  },
  deleteDocument: (id: number): Promise<void> => {
    return apiClient.delete(`/documents/${id}`).then(() => {});
  },
  moveToFolder: (id: number, folderId: number | null): Promise<void> => {
    return apiClient.patch(`/documents/${id}/folder`, { folder_id: folderId }).then(() => {});
  },
};

// Excel Document Folders API
export const docFoldersAPI = {
  listFolders: (): Promise<any[]> => {
    return apiClient.get('/doc-folders').then(r => r.data);
  },
  createFolder: (data: { name: string; color?: string }): Promise<any> => {
    return apiClient.post('/doc-folders', data).then(r => r.data);
  },
  updateFolder: (id: number, data: { name?: string; color?: string; position?: number }): Promise<any> => {
    return apiClient.patch(`/doc-folders/${id}`, data).then(r => r.data);
  },
  deleteFolder: (id: number): Promise<void> => {
    return apiClient.delete(`/doc-folders/${id}`).then(() => {});
  },
};

// Editor AI API
export const editorAPI = {
  aiCommand: (data: {
    command: string;
    sheet_context: any;
    sheet_name?: string;
    selection?: any;
    all_sheet_names?: string[];
  }): Promise<any> => {
    return apiClient.post('/editor/ai-command', data).then(r => r.data);
  },
  uploadFile: (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/editor/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  exportXlsx: async (sheetData: any[], fileName: string): Promise<void> => {
    const response = await apiClient.post('/editor/export-xlsx',
      { sheet_data: sheetData, file_name: fileName },
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

/**
 * Build a backend OAuth login URL with first-touch attribution
 * appended as query params. The backend writes them to session
 * before redirecting to the OAuth provider.
 *
 * Always relative — the prod build of REACT_APP_API_URL ended up
 * containing `http://localhost:10001` (left over from local dev),
 * which sent users to localhost. The browser/proxy resolves a
 * relative `/api/v1/...` against the current host and Traefik
 * forwards it to the backend correctly. apiClient already does the
 * same: `axios.create({ baseURL: '/api/v1' })`.
 */
export function buildOAuthLoginUrl(provider: 'google' | 'yandex'): string {
  const path = `/api/v1/auth/${provider}`;
  const attribution = getAttributionPayload();
  const params = new URLSearchParams();
  if (attribution.utm_source) params.set('utm_source', attribution.utm_source);
  if (attribution.utm_medium) params.set('utm_medium', attribution.utm_medium);
  if (attribution.utm_campaign) params.set('utm_campaign', attribution.utm_campaign);
  if (attribution.utm_content) params.set('utm_content', attribution.utm_content);
  if (attribution.utm_term) params.set('utm_term', attribution.utm_term);
  if (attribution.ref) params.set('ref', attribution.ref);
  if (attribution.referrer_url) params.set('referrer_url', attribution.referrer_url);
  // Read fingerprint synchronously from localStorage cache (warmed on app mount)
  const fp = getDeviceFingerprintSync();
  if (fp) params.set('device_fingerprint', fp);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export default apiClient;
